import { useCallback, useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { formatarData } from "../lib/dados";
import { carregarMeuPerfil } from "../lib/perfil";
import { faltamResponder, lerMinhasPesquisas, responderPesquisa, type PesquisaDoAluno } from "../lib/pesquisas";
import { Aviso, Carregando, Topo } from "../componentes/ui";
import FormularioDeNota from "../componentes/FormularioDeNota";
import Rodape from "../componentes/Rodape";

// Avaliações — as pesquisas de satisfação dos cursos do aluno (000310, o G1 do
// Plano de Melhorias da Equipe de Produtos).
//
// Uma porta SÓ, no menu da conta, e não uma aba em cada curso: a pesquisa é do
// curso, mas quem responde é a pessoa, e duas pesquisas de dois cursos numa
// tela só custam menos cliques que duas abas. A página do curso AVISA quando há
// pesquisa aberta sobre ele, e o aviso traz o aluno até aqui.
//
// O que a tela promete, e cumpre: a equipe vê a NOTA e o COMENTÁRIO, nunca o
// nome de quem respondeu. A promessa vem ANTES do formulário, porque é ela que
// decide o que a pessoa vai escrever. Desde a 000318 (o C7) a avaliação de um
// PROFESSOR vai também para a Central de Professores, onde ele a vê — e a
// promessa diz isso antes de ele escrever, porque o leitor mudou.
export default function Avaliacoes() {
  const [pesquisas, setPesquisas] = useState<PesquisaDoAluno[] | null>(null);
  const [eu, setEu] = useState<{ nome: string | null; avatar: string | null }>({ nome: null, avatar: null });
  const [estado, setEstado] = useState<"carregando" | "pronto" | "erro">("carregando");

  const recarregar = useCallback(async () => {
    const lista = await lerMinhasPesquisas();
    if (lista) setPesquisas(lista);
    return lista;
  }, []);

  useEffect(() => {
    let ativo = true;
    (async () => {
      // Duas chamadas, e não o `useHub`: ele faz sete para uma tela que precisa
      // das pesquisas e do nome no Topo (o desenho de Meus documentos).
      const [lista, perfil] = await Promise.all([lerMinhasPesquisas(), carregarMeuPerfil().catch(() => null)]);
      if (!ativo) return;
      if (!lista) {
        setEstado("erro");
        return;
      }
      setPesquisas(lista);
      setEu({ nome: perfil?.nome ?? null, avatar: perfil?.avatar_url ?? null });
      setEstado("pronto");
    })();
    return () => {
      ativo = false;
    };
  }, []);

  if (estado === "carregando") return <Carregando texto="Abrindo as suas avaliações…" />;
  if (estado === "erro" || !pesquisas) {
    return (
      <Aviso titulo="Algo deu errado" acao={<Link to="/" className="btn-contorno">Voltar ao início</Link>}>
        Não conseguimos abrir as avaliações agora. Recarregue a página; se continuar, fale com o suporte.
      </Aviso>
    );
  }

  const pendentes = pesquisas.reduce((s, p) => s + faltamResponder(p), 0);

  return (
    <div className="min-h-screen">
      <Topo atual="inicio" nome={eu.nome} avatarUrl={eu.avatar} />

      <div className="mx-auto max-w-3xl px-4 pt-10 pb-16 sm:px-6">
        <h1 className="font-display text-3xl font-bold text-titulo">Avaliações</h1>
        <p className="mt-2 max-w-2xl text-cinza">
          As pesquisas de satisfação dos seus cursos. A equipe vê a sua nota e o seu comentário —{" "}
          <strong className="font-semibold text-tinta">não o seu nome</strong>. Na avaliação de um professor,
          ele também vê a nota e o comentário que você der a ele, sem saber quem escreveu. Você pode mudar a
          resposta enquanto a pesquisa estiver aberta.
        </p>

        {pesquisas.length === 0 ? (
          <div className="surface mt-8 p-6" data-sem-pesquisas>
            <h2 className="font-display text-lg font-bold text-titulo">Nenhuma pesquisa aberta agora</h2>
            <p className="mt-2 text-sm text-cinza">
              Quando a equipe abrir uma pesquisa sobre um dos seus cursos, ela aparece aqui — e a página do
              curso avisa.
            </p>
          </div>
        ) : (
          <>
            <p className="mt-6 text-sm text-tinta" data-pendentes={pendentes}>
              {pendentes === 0
                ? "Você já respondeu tudo que está aberto. Obrigado!"
                : pendentes === 1
                  ? "Falta 1 resposta."
                  : `Faltam ${pendentes} respostas.`}
            </p>
            <div className="mt-4 flex flex-col gap-6">
              {pesquisas.map((p) => (
                <CartaoDaPesquisa key={p.id} pesquisa={p} aoResponder={recarregar} />
              ))}
            </div>
          </>
        )}

        <Rodape />
      </div>
    </div>
  );
}

function CartaoDaPesquisa({ pesquisa, aoResponder }: { pesquisa: PesquisaDoAluno; aoResponder: () => Promise<unknown> }) {
  const idTitulo = useId();
  return (
    <section
      id={`pesquisa-${pesquisa.id}`}
      role="group"
      aria-labelledby={idTitulo}
      className="surface rise p-5 sm:p-6"
      data-pesquisa={pesquisa.id}
    >
      {/* <section role="group"> em vez de <fieldset><legend>: o legend sai
          flutuando sobre a borda do card arredondado (a regra de 19/08). */}
      <p className="text-xs font-bold tracking-widest text-cinza uppercase">{pesquisa.curso_nome}</p>
      <h2 id={idTitulo} className="mt-1 font-display text-lg font-bold text-titulo">
        {pesquisa.titulo}
      </h2>
      <p className="mt-1 text-sm text-tinta">{pesquisa.pergunta}</p>
      {pesquisa.fecha_em && (
        <p className="mt-1 text-xs text-cinza">Aberta até {formatarData(pesquisa.fecha_em)}.</p>
      )}

      {pesquisa.alvo === "curso" ? (
        <FormularioDeNota
          chave={`${pesquisa.id}-curso`}
          rotulo={pesquisa.titulo}
          atual={pesquisa.resposta}
          enviar={(nota, comentario) => responderPesquisa(pesquisa.id, null, nota, comentario)}
          aoResponder={aoResponder}
        />
      ) : (pesquisa.docentes ?? []).length === 0 ? (
        <p className="mt-4 text-sm text-cinza" data-sem-professores>
          Esta pesquisa é sobre os professores da sua turma, e não achamos uma turma com professores na sua
          matrícula deste curso. Se isso parece errado, fale com o suporte.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-borda">
          {(pesquisa.docentes ?? []).map((d) => (
            <li key={d.id} className="py-4" data-docente={d.nome}>
              <p className="font-semibold text-tinta">{d.nome}</p>
              <FormularioDeNota
                chave={`${pesquisa.id}-${d.id}`}
                rotulo={d.nome}
                atual={d.resposta}
                enviar={(nota, comentario) => responderPesquisa(pesquisa.id, d.id, nota, comentario)}
                aoResponder={aoResponder}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
